import { useMutation } from '@apollo/client'
import { UpdatePropertiesForPUCInput } from '@deltek/specification-client/core/Models/uniformatClassificationProperty'
import { UpdatePropertyValuesForUniformatClassificationMutation } from 'api/graphQL/mutations/projectElement.mutations'
import { UpdatePUCPropertyValuesResponse } from 'api/graphQL/types/projectElement.types'
import { useNotification } from 'components/WorkspaceEditor/services'
import {
    useSelectedProjectElementNode,
    useWorkspace2Actions,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useEffect } from 'react'

/** Props for useAssemblyTextMutationHandler hook */
interface UseAssemblyTextMutationHandlerProps {
    /** Project ID for the mutation */
    projectId: string | null
}

/**
 * Hook for handling assembly text mutations with notifications
 * Updates uniformat classification properties and shows loading/success/error states
 */
export const useAssemblyTextMutationHandler = ({
    projectId,
}: UseAssemblyTextMutationHandlerProps) => {
    const notification = useNotification()
    const selectedProjectElementNode = useSelectedProjectElementNode()
    const { setSelectedElementData } = useWorkspace2Actions()
    
    const [updatePUCPropertyValues, { loading }] = useMutation<
        UpdatePUCPropertyValuesResponse,
        { input: UpdatePropertiesForPUCInput }
    >(UpdatePropertyValuesForUniformatClassificationMutation, {
        onCompleted: (data) => {
            setSelectedElementData?.(
                data.updatePropertyValuesForUniformatClassification
            )
            notification?.success?.({
                message: 'Changes Saved Successfully',
            })
        },
        onError: (error) => {
            notification?.error?.({ message: error.message ?? '' })
        },
    })

    /** Updates a property value for the selected project element */
    const handleUpdatePUCPropertyValue = useCallback(
        (elementDataId: string, propertyValue: string) => {
            if (selectedProjectElementNode) {
                if (!selectedProjectElementNode.elementId) return
                updatePUCPropertyValues({
                    variables: {
                        input: {
                            projectUniformatClassificationId:
                                selectedProjectElementNode.elementId,
                            uniformatClassificationPropertyInputs: [
                                {
                                    id: elementDataId,
                                    value: propertyValue ?? '',
                                },
                            ],
                            projectId: projectId ?? '',
                        },
                    },
                })
            }
        },
        [selectedProjectElementNode, projectId, updatePUCPropertyValues]
    )
    /** Shows loading notification when mutation is in progress */
    useEffect(() => {
        if (loading) notification?.loading?.({ message: 'Saving...' })
    }, [loading, notification])

    return {
        handleUpdatePUCPropertyValue,
    }
}
